import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { pricingService } from '../services/pricingService';
import '../styles/UserProfile.css';

const UserProfile = ({ user, onSignOut, onShowPricing }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [userUsage, setUserUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadUserUsage = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        const usage = await pricingService.getUserUsage(session.access_token);
        setUserUsage(usage);
      }
    } catch (error) {
      console.error('Error loading user usage:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadUserUsage();
    }
  }, [user, loadUserUsage]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      onSignOut();
      setShowDropdown(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getTierColor = (tier) => {
    const colors = { free: '#3e3e40', premium: '#6BFAC8', pro: '#3DD9A5' };
    return colors[tier] || colors.free;
  };

  const getTierBadge = (tier) => {
    const badges = { free: 'Free', premium: 'Premium', pro: 'Pro' };
    return badges[tier] || badges.free;
  };

  const displayName = user?.user_metadata?.full_name || user?.email || 'User';
  const avatarUrl = user?.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=101218&color=6BFAC8`;

  return (
    <div className="user-profile-container">
      <div
        className="user-profile-trigger"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <img src={avatarUrl} alt="Profile" className="user-avatar" />
        <div className="user-info">
          <span className="user-name">{displayName}</span>
          {userUsage && (
            <span className="user-tier" style={{ color: getTierColor(userUsage.tier) }}>
              {getTierBadge(userUsage.tier)}
            </span>
          )}
        </div>
        <svg
          className={`dropdown-arrow ${showDropdown ? 'rotated' : ''}`}
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        >
          <polyline points="6,9 12,15 18,9"></polyline>
        </svg>
      </div>

      {showDropdown && (
        <div className="user-dropdown">
          <div className="dropdown-header">
            <img src={avatarUrl} alt="Profile" className="dropdown-avatar" />
            <div>
              <div className="dropdown-name">{displayName}</div>
              <div className="dropdown-email">{user?.email}</div>
            </div>
          </div>

          {loading ? (
            <div className="usage-loading"><div className="spinner"></div>Loading usage...</div>
          ) : userUsage ? (
            <div className="usage-info">
              <div className="usage-header">
                <span>Current Usage</span>
                <span className="tier-badge" style={{ backgroundColor: getTierColor(userUsage.tier) }}>
                  {getTierBadge(userUsage.tier)}
                </span>
              </div>
              <div className="usage-stats">
                <div className="usage-bar-container">
                  <div className="usage-bar" style={{
                    width: `${Math.min((userUsage.current / userUsage.limit) * 100, 100)}%`,
                    backgroundColor: userUsage.current >= userUsage.limit ? '#ef4444' : getTierColor(userUsage.tier)
                  }}></div>
                </div>
                <div className="usage-text">{userUsage.current} / {userUsage.limit === -1 ? 'Unlimited' : userUsage.limit} analyses</div>
                {userUsage.resetInfo?.type === 'rolling' && (
                  <div className="reset-info">Resets every {userUsage.resetInfo.intervalHours} hours</div>
                )}
                {userUsage.resetInfo?.type === 'daily' && (
                  <div className="reset-info">Resets daily at midnight</div>
                )}
              </div>
            </div>
          ) : null}

          <div className="dropdown-actions">
            <button className="dropdown-button pricing-button" onClick={() => { onShowPricing(); setShowDropdown(false); }}>
              View Pricing
            </button>
            <button className="dropdown-button signout-button" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        </div>
      )}

      {showDropdown && <div className="dropdown-backdrop" onClick={() => setShowDropdown(false)} />}
    </div>
  );
};

export default UserProfile;
